import { useEffect } from 'react';
import { BrandHero, CtaBand, SectionHeader } from '@/components/brand/BrandKit';

const HERO_IMG = "/generated/partners-hero.jpg";

const partners = [
  { name: "Europcar Mobility Group", sector: "Global Mobility" },
  { name: "Goldcar", sector: "Global Mobility" },
  { name: "Al Khoory Automobiles", sector: "Automotive Partner" },
  { name: "IMT Dubai", sector: "Events & Entertainment" },
  { name: "ADNOC", sector: "Energy & Government" },
  { name: "Esaad", sector: "Government Programme" },
  { name: "Fazaa", sector: "Government Programme" },
  { name: "Al Saada Card", sector: "Government Programme" },
  { name: "GIG Insurance", sector: "Insurance" },
  { name: "QIC (Qatar Insurance)", sector: "Insurance" },
  { name: "Oman Insurance", sector: "Insurance" },
  { name: "Tokio Marine", sector: "Insurance" },
  { name: "Noor Takaful", sector: "Insurance" },
];

const customers = {
  "Aviation": [
    "Emirates", "Etihad Airways", "dnata", "Air France", "Virgin Atlantic",
    "KLM", "Lufthansa", "Cathay Pacific", "South African Airways"
  ],
  "Hospitality": [
    "Jumeirah", "Emaar", "Atlantis The Palm", "The Address Hotels + Resorts",
    "Hyatt", "JA Resorts & Hotels", "Sofitel Luxury Hotels", "Hilton Worldwide"
  ],
  "Government & Municipal": [
    "Dubai Municipality"
  ],
  "Cruise & Maritime": [
    "Royal Caribbean International", "MSC Cruises", "Costa Cruises",
    "Carnival", "Cunard", "Black Watch"
  ],
  "Corporate & Technology": [
    "General Electric", "Samsung", "LG", "Panasonic", "Noon",
    "Al Naboodah", "Al Habtoor Group", "MBC Group", "Desert Adventures", "Ekar"
  ],
  "Travel & Events": [
    "Sharaf Tours", "Al Rainab", "Barakat", "Pan Home Furnishings"
  ],
};

const majorEvents = [
  "Dubai International Film Festival", "PGA European Tour", "Cirque du Soleil",
  "DP World", "Dubai World Cup", "Emirates Dubai Rugby 7s", "Global Village",
  "Expo 2020 Dubai, UAE", "Dubai Airshow", "International Monetary Fund",
  "RISE 2015 Dubai", "Dubai Calendar", "Atlantic Council", "Dior"
];

export default function PartnersClientsPage() {
  useEffect(() => { document.title = "Our Clients | Eurogulf Mobility Group | Trusted by UAE's Leading Brands"; }, []);

  return (
    <div data-testid="partners-clients-page">
      <BrandHero testid="partners-hero" chip="Trusted Partnerships" title="The mobility partner behind the UAE's most respected organisations."
        lead="For 50 years, Eurogulf Mobility Group has been the mobility partner of choice for the UAE's most respected organisations, from airlines and luxury hotels to government entities and global corporations."
        image={HERO_IMG} imageAlt="Business partners shaking hands in front of a corporate fleet in Dubai" position="center 30%"
        ctas={[{ label: 'Collaborate With Us', href: '/collaborate', testid: 'partners-hero-cta' }]} />

      {/* PARTNERS */}
      <section data-testid="partners-grid" className="bg-white py-14 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader compact chip="Our Partners" title="Alliances that extend our reach." desc="Strategic alliances and programme partnerships that extend our reach and value to customers across the UAE." align="center" />
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px overflow-hidden rounded-xl border border-black/[0.06] bg-black/[0.06]">
            {partners.map((p) => (
              <div key={p.name} className="bg-white p-5 text-center hover:bg-[#FFF4ED]/70 transition-colors">
                <h3 className="font-heading font-bold text-sm text-[#121212]">{p.name}</h3>
                <p className="mt-1 font-heading font-bold text-[10px] tracking-[0.12em] uppercase text-[#EE5A01]">{p.sector}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLIENTS BY SECTOR */}
      <section data-testid="clients-section" className="bg-[#FAFAFA] border-y border-black/5 py-14 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader compact chip="Our Customers" title="Serving the UAE's most recognised brands." desc="Across aviation, hospitality, government, and enterprise." align="center" />
          <div className="mt-12 space-y-10">
            {Object.entries(customers).map(([sector, names]) => (
              <div key={sector}>
                <h3 className="font-heading font-bold text-[11px] text-[#EE5A01] uppercase tracking-[0.2em] mb-4 text-center">{sector}</h3>
                <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
                  {names.map((name) => (
                    <span key={name} className="font-heading font-bold text-sm text-[#121212]/40 hover:text-[#121212] transition-colors uppercase tracking-wider">{name}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MAJOR EVENTS */}
      <section data-testid="events-section" className="bg-white py-14 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader compact chip="Major Events" title="The events that define Dubai. The mobility behind them." desc="The UAE's most high-profile events rely on Eurogulf Mobility Group for their transportation logistics." align="center" />
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            {majorEvents.map((e) => (
              <span key={e} className="rounded-full border border-black/10 bg-white px-5 py-2.5 font-heading font-bold text-xs text-[#121212] uppercase tracking-wider hover:border-[#EE5A01] hover:text-[#EE5A01] transition-colors">{e}</span>
            ))}
          </div>
        </div>
      </section>

      <CtaBand title="Become a partner." desc="Looking for a trusted mobility partner for your business? Let's discuss how Eurogulf Mobility Group can move your organisation forward."
        ctas={[{ label: 'Contact Our Team', href: '/contact', testid: 'partners-cta' }, { label: 'Explore Partnerships', href: '/collaborate', variant: 'outline' }]} />
    </div>
  );
}
